import { useMediaQuery } from '@uhg-abyss/web/hooks/useMediaQuery';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Text } from '@uhg-abyss/web/ui/Text';

import { adobeLinkTrackEvent } from '../../../../common/AdobeTagging/adobeLinkTrackEvent';
import { Constants } from '../../../../common/Constants';
import { mobileOnly } from '../../../../common/ConstantsStyles';
import { ContentWrapper } from '../../../../common/ContentWrapper';
import { ContainerForAllSectionsStyled } from '../../../../common/ProviderDetailTabs/ProviderDetailTabs.styled';
import { useAdobePageTrackEvent } from '../../../../hooks/adobeHook/useAdobePageTrackEvent';
import { useLagoon } from '../../../../hooks/useLagoon';
import {
  getClaimOrEligibilitySystemTypeCode,
  getCurrentMember,
  getPolicyNumber,
} from '../../../../utils/user.utils';
import {
  StyledVisionCareBox,
  StyledVisionCareButton,
} from './ExternalVisionCard.styled';

/**
 * Checks if the given URL is an absolute URL (starts with "http") or a relative URL.
 * @param url - The URL to check.
 * @returns True if the URL is absolute, false if it is relative.
 */
const isAbsoluteUrl = (url: string): boolean => url.startsWith('http');

/**
 * Helper function to check if a value matches a comma-separated list or single value
 */
const doCompare = (contentVal: string, memberVal: string) => {
  if (!contentVal || !memberVal) return false;
  if (contentVal?.includes(',')) {
    const contentValAr = contentVal.split(',').map((v) => v.trim());
    return contentValAr.includes(memberVal);
  }
  return memberVal === contentVal;
};

/**
 * Gets the C&S vision vendor content based on member type and eligibility.
 * - DSNP members: Match by HPBP codes (hpbpCodes or hbppCode field)
 * - Medicaid members (not DSNP): Match by PolicyIds, fallback to HPBP codes
 */
const getCnsVisionContent = (
  visionCareCard: any[],
  isCnSDsnpMember: boolean,
  memberHPBP?: string,
  memberPolicyId?: string
) => {
  const getHpbpCodes = (content: any) =>
    content.hpbpCodes || content.hbppCode || '';

  if (isCnSDsnpMember) {
    const dsnpContent = visionCareCard.find(
      (content) =>
        getHpbpCodes(content) &&
        memberHPBP &&
        doCompare(getHpbpCodes(content), memberHPBP)
    );
    if (dsnpContent) {
      return dsnpContent;
    }
  } else {
    const medicaidContent = visionCareCard.find(
      (content) =>
        content.policyIDs &&
        memberPolicyId &&
        doCompare(content.policyIDs, memberPolicyId)
    );
    if (medicaidContent) {
      return medicaidContent;
    }

    const medicaidHpbpContent = visionCareCard.find(
      (content) =>
        getHpbpCodes(content) &&
        memberHPBP &&
        doCompare(getHpbpCodes(content), memberHPBP)
    );
    if (medicaidHpbpContent) {
      return medicaidHpbpContent;
    }
  }

  return null;
};

export const ExternalVisionCard = () => {
  const currentMember = getCurrentMember();
  const mobileScreen = useMediaQuery(mobileOnly);
  const visionCareCard = useLagoon(Constants.LAGOON_TABLE.VISION_CARE_CARD)();

  const { adobePageTrackEvent } = useAdobePageTrackEvent({
    pageName: 'vision overview',
    impressionBlock: {
      type: 'provider',
    },
  });

  const visionCoverage = getClaimOrEligibilitySystemTypeCode(
    currentMember,
    'V'
  );
  const memberSitusState = visionCoverage?.situsState;
  const memberPolicyId = getPolicyNumber(currentMember, 'V');

  const coverageType =
    currentMember?.eligibility?.[0]?.memberHealthCoverage?.coverageType?.[0];
  const memberHPBP = `${coverageType?.contractNumber ?? ''}-${
    coverageType?.pbpNumber ?? ''
  }-${coverageType?.segmentId ?? ''}`;
  const cnsVisionContent = currentMember?.isCnSMember
    ? getCnsVisionContent(
        visionCareCard,
        currentMember?.isCnSDsnpMember,
        memberHPBP,
        memberPolicyId
      )
    : null;

  const planSpecificContent = visionCareCard.find((content) => {
    const { policyIDs, situsState } = content;
    if (situsState) {
      if (!doCompare(situsState, memberSitusState)) {
        return false;
      }

      if (policyIDs) {
        return doCompare(policyIDs, memberPolicyId);
      }
      return true;
    }

    if (policyIDs) {
      return doCompare(policyIDs, memberPolicyId);
    }
    return false;
  });

  const defaultContent = visionCareCard.find(
    (content) => !content.situsState && !content.policyIDs && !content.hpbpCodes
  );

  const visionCardContent =
    cnsVisionContent || planSpecificContent || defaultContent;

  if (!visionCardContent) {
    return null;
  }

  const { title, message, url, search, buttonTitle } = visionCardContent;
  const urlWithSearchParam = search
    ? `${url}&search=${search}&searchType=speciality&psx-source=${Constants.SEARCH_SOURCE_TYPES.SPECIALTY_CATEGORY}`
    : url;
  const boxWidth = mobileScreen ? '100%' : '588px';

  const handleOnClick = (): void => {
    adobeLinkTrackEvent({
      name: buttonTitle,
      location: title,
      type: 'external',
    });
    adobePageTrackEvent();
  };

  return (
    <ContainerForAllSectionsStyled>
      <ContentWrapper>
        <StyledVisionCareBox
          color="$core.color.aqua.15"
          data-testid="external-vision-card-container"
          padding="16px 16px 16px 24px"
          width={boxWidth}
        >
          <Layout.Stack alignItems="left" space={0}>
            <Text
              color="$core.color.brand.100"
              data-testid="external-vision-card-heading"
              fontSize={mobileScreen ? 'md' : 'lg'}
              fontWeight="bold"
            >
              {title}
            </Text>
            <Text
              color="$core.color.neutral.80"
              data-testid="external-vision-card-content"
              fontSize={mobileScreen ? 'sm' : 'md'}
              fontWeight={mobileScreen ? 'regular' : 'medium'}
            >
              {message}
            </Text>
            <StyledVisionCareButton
              data-testid="cns-vision-redirect-button"
              href={urlWithSearchParam}
              onClick={handleOnClick}
              openNewWindow={isAbsoluteUrl(urlWithSearchParam)}
            >
              {buttonTitle}
            </StyledVisionCareButton>
          </Layout.Stack>
        </StyledVisionCareBox>
      </ContentWrapper>
    </ContainerForAllSectionsStyled>
  );
};
